import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useUser } from '@clerk/clerk-react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupsIcon from '@mui/icons-material/Groups';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { DeleteModal } from 'components/DeleteModal';
import { EditAgentModal } from 'components/EditAgentModal/EditAgentModal';
import { IntegrationStatus } from 'components/IntegrationStatus';
import { TeamMemberManagement } from 'components/TeamMemberManagement';
import { useIntegrationCallback } from '../../hooks/useIntegrationCallback';
import { useApi } from '../../hooks/useApi';

import { Agent } from '../../types/agents';

export const AgentsPage = () => {
  const { user } = useUser();
  const { apiFetch } = useApi();
  const [agentId, setAgentId] = useState<string | null>(null);
  const [editAgent, setEditAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean>>({});

  useIntegrationCallback();

  const fetchAgents = async () => {
    try {
      const res = await apiFetch('agent-list');
      const data: Agent[] = await res.json();
      setAgents(data);
      
      // Check connection status for each agent
      data.forEach(agent => checkConnectionStatus(agent.id));
    } catch (e) {
      console.log('Something went wrong', e);
    }
  };

  const checkConnectionStatus = async (agentId: string) => {
    try {
      const res = await apiFetch(`integrations/${agentId}/teams/status`);
      if (res.ok) {
        const data = await res.json();
        setConnectionStatus(prev => ({ ...prev, [agentId]: data.connected }));
      }
    } catch (e) {
      console.log('Error checking connection status:', e);
    }
  };

  const toggleExpanded = (agentId: string) => {
    setExpandedAgents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  const handleConnectionChange = (agentId: string, connected: boolean) => {
    setConnectionStatus(prev => ({ ...prev, [agentId]: connected }));
  };

  useEffect(() => {
    if (user?.id) {
      fetchAgents();
    }
  }, [user?.id]);

  const toggleDeleteModal = (id: string | null) => {
    setAgentId(id);
  };

  const deleteAgent = async () => {
    setIsLoading(true);

    try {
      const res = await apiFetch(`agent/${agentId}`, {
        method: 'DELETE',
      });

      const data = await res.json();
      fetchAgents();

      const agentIdStorage = localStorage.getItem('agent');

      if (agentIdStorage === String(agentId)) {
        localStorage.removeItem('agent');
      }

      toggleDeleteModal(null);

      toast.success(data.message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An unexpected error occurred';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Stack spacing={4}>
        {/* Page Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
              Agents
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Manage your AI agents
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            href="/instructions"
            sx={{
              backgroundColor: '#0099CC',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#007AA3',
              },
            }}
          >
            New Agent
          </Button>
        </Stack>

        {/* Agents Grid */}
        {agents.length === 0 ? (
          <Card
            sx={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 153, 204, 0.1)',
              p: 4,
              textAlign: 'center',
            }}
          >
            <SmartToyIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6B7280' }}>
              No agents yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3 }}>
              Create your first agent to get started
            </Typography>
            <Button
              variant="contained"
              href="/instructions"
              sx={{
                backgroundColor: '#0099CC',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#007AA3',
                },
              }}
            >
              Create Agent
            </Button>
          </Card>
        ) : (
          <Stack spacing={2}>
            {agents.map((agent) => (
              <Card
                key={agent.id}
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 153, 204, 0.1)',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 153, 204, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 3, pb: expandedAgents.has(agent.id) ? 0 : 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                          {agent.name}
                        </Typography>
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            backgroundColor: '#D1FAE5',
                            color: '#065F46',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6B7280',
                          mb: 2,
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                        }}
                      >
                        {agent.endpoint}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setEditAgent(agent)}
                          sx={{
                            borderColor: '#E5E7EB',
                            color: '#374151',
                            textTransform: 'none',
                            borderRadius: '6px',
                            '&:hover': {
                              borderColor: '#0099CC',
                              backgroundColor: 'rgba(0, 153, 204, 0.04)',
                            },
                          }}
                        >
                          Edit
                        </Button>
                        {connectionStatus[agent.id] && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GroupsIcon sx={{ fontSize: 16 }} />}
                            endIcon={expandedAgents.has(agent.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => toggleExpanded(agent.id)}
                            sx={{
                              borderColor: '#0099CC',
                              color: '#0099CC',
                              textTransform: 'none',
                              borderRadius: '6px',
                              '&:hover': {
                                borderColor: '#007AA3',
                                backgroundColor: 'rgba(0, 153, 204, 0.04)',
                              },
                            }}
                          >
                            Team Members
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PowerSettingsNewIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            borderColor: '#E5E7EB',
                            color: '#374151',
                            textTransform: 'none',
                            borderRadius: '6px',
                            '&:hover': {
                              borderColor: '#0099CC',
                              backgroundColor: 'rgba(0, 153, 204, 0.04)',
                            },
                          }}
                        >
                          Disable
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => toggleDeleteModal(agent.id)}
                          sx={{
                            border: '1px solid #FEE2E2',
                            borderRadius: '6px',
                            color: '#EF4444',
                            '&:hover': {
                              backgroundColor: '#FEF2F2',
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    </Box>
                    <IntegrationStatus 
                      agentId={agent.id} 
                      onConnectionChange={(connected) => handleConnectionChange(agent.id, connected)}
                    />
                  </Stack>
                </CardContent>
                
                {/* Team Member Management Panel */}
                <Collapse in={expandedAgents.has(agent.id)} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 3, pb: 3 }}>
                    <TeamMemberManagement 
                      agentId={agent.id} 
                      isConnected={connectionStatus[agent.id] || false} 
                    />
                  </Box>
                </Collapse>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
      <DeleteModal
        title="Are you sure you want to delete this agent along with all its associated tasks?"
        onClose={() => toggleDeleteModal(null)}
        onConfirm={deleteAgent}
        isOpen={!!agentId}
        isLoading={isLoading}
      />
      <EditAgentModal
        isOpen={!!editAgent}
        onClose={() => setEditAgent(null)}
        agent={editAgent}
        onSuccess={fetchAgents}
      />
    </Container>
  );
};
